import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import FileViewerModal from "components/Common/FileViewerModal";
import VehicleInfoCell from "components/Common/VehicleInfo/VehicleInfoCell";
import { formatReadableDate } from "utils/formatDate";
import { brtaRegLabel } from "api/brtaRegistrationApi";

const SLOT_LABELS = {
  lc_documents: "LC Documents",
  customer_tin: "Customer TIN",
  customer_nid: "Customer NID",
  brta_papers: "BRTA Papers",
};

const SLOT_KEYS = Object.keys(SLOT_LABELS);

const isPdf = item =>
  String(item?.file ? item.file.name : item.name || "")
    .toLowerCase()
    .endsWith(".pdf");

const BrtaRegistrationViewModal = ({ isOpen = false, registration = null, onClose = () => {} }) => {
  const [viewFile, setViewFile] = useState(null);

  const attachments = registration?.attachments || {};

  const renderInfoRow = (label, value) => (
    <div className="d-flex mb-2">
      <span className="text-muted fw-medium" style={{ width: 140 }}>
        {label}
      </span>
      <span className="text-body">{value || "-"}</span>
    </div>
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose} tag="h4">
        BRTA Registration Details
      </ModalHeader>
      <ModalBody>
        {registration && (
          <>
            <div className="mb-4">
              <h6 className="text-muted text-uppercase fw-semibold small mb-2">
                Vehicle
              </h6>
              <VehicleInfoCell data={registration.vehicle || {}} />
            </div>

            <div className="mb-4">
              <h6 className="text-muted text-uppercase fw-semibold small mb-2">
                Registration Info
              </h6>
              <div className="border rounded-3 p-3">
                {renderInfoRow(
                  "BRTA Number",
                  brtaRegLabel(registration) || "No BRTA Number"
                )}
                {renderInfoRow(
                  "Registered By",
                  registration.registeredBy?.name ||
                    registration.registeredBy?.email
                )}
                {renderInfoRow(
                  "Created",
                  registration.created_at
                    ? formatReadableDate(registration.created_at)
                    : null
                )}
              </div>
            </div>

            <div>
              <h6 className="text-muted text-uppercase fw-semibold small mb-2">
                Attachments
              </h6>
              {SLOT_KEYS.map(slotKey => {
                const items = attachments[slotKey] || [];
                if (items.length === 0) return null;
                return (
                  <div key={slotKey} className="mb-3">
                    <span className="fw-semibold d-block mb-2">
                      {SLOT_LABELS[slotKey]}
                      <span className="text-muted fw-normal small ms-2">
                        ({items.length})
                      </span>
                    </span>
                    <div className="d-flex flex-wrap gap-3">
                      {items.map((item, idx) => {
                        const pdf = isPdf(item);
                        return (
                          <div
                            key={`${slotKey}-${idx}`}
                            className="text-center"
                            style={{ width: 110 }}
                          >
                            <button
                              type="button"
                              className="btn btn-soft-info p-0 border-0 d-block w-100"
                              onClick={() =>
                                setViewFile({ url: item.url, name: item.name })
                              }
                              title="View"
                            >
                              {pdf ? (
                                <div className="d-flex align-items-center justify-content-center rounded border bg-light" style={{ height: 76 }}>
                                  <i className="mdi mdi-file-pdf-outline text-danger fs-2" />
                                </div>
                              ) : (
                                <img
                                  src={item.url}
                                  alt={item.name}
                                  className="rounded border w-100"
                                  style={{ height: 76, objectFit: "cover" }}
                                />
                              )}
                            </button>
                            <span
                              className="text-truncate d-block small mt-1 text-muted"
                              title={item.name}
                            >
                              {item.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {SLOT_KEYS.every(slotKey => (attachments[slotKey] || []).length === 0) && (
                <div className="text-muted small">No attachments yet.</div>
              )}
            </div>

            <FileViewerModal
              isOpen={!!viewFile}
              onClose={() => setViewFile(null)}
              fileUrl={viewFile?.url}
              fileName={viewFile?.name || "Attachment"}
            />
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default BrtaRegistrationViewModal;
